import React, { useState, useEffect, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import InputLabel from "@mui/material/InputLabel"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormHelperText from "@mui/material/FormHelperText"
import Switch from "@mui/material/Switch"
import Grid from "@mui/material/Grid"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Alert from "@mui/material/Alert"
import CircularProgress from "@mui/material/CircularProgress"
// import Chip from "@mui/material/Chip"
import { useSnackbar } from "notistack"
import { useForm, Controller } from "react-hook-form"
import { verifyResponse, httpPost } from "utils/httpRequest"
import RichTextEditor from "components/common/RichTextEditor"
import {
  EVENT_TYPE_LIST,
  EVENT_PLATFORM_LIST,
  EVENT_STATUS_LIST,
  TIMEZONE_LIST,
} from "constants/global"
import moment from "moment"
import "moment-timezone"
import {
  createEvent,
  updateEvent,
  getEventById,
  createZoomMeeting,
  getAudienceEmailEstimate,
} from "services/event"
import { getTierPrograms } from "services/tierProgram"
import { getProducts } from "services/products"
import { getFullCategories } from "services/category"
import { searchResource } from "services/media"
import LoadingScreen from "components/common/LoadingScreen"
import MediaManager from "components/common/MediaManager"
import TagsInput from "components/common/TagsInput"
import EventEmailCadence from "components/common/EventEmailCadence"
import { convertUTCToTimezoneForInput } from "utils/time"
import { buildPlatformEventUrl, trackAdminEventCreated } from "utils/hubspotTracking"
import { StyledEventDialog } from "./Event.style"

const DEFAULT_UPLOAD_BUCKET =
  process.env.REACT_APP_S3_UPLOAD_BUCKET || "innercise-media-upload"

const normalizeTimezone = (timezone) => {
  if (!timezone) return "America/Los_Angeles"
  const match = TIMEZONE_LIST.find(
    (tz) => tz.value === timezone || tz.text === timezone
  )
  return match ? match.value : timezone
}

const removeEmptyRichTextParagraphs = (html = "") =>
  html
    .replace(
      /<p(?:\s[^>]*)?>\s*(?:(?:<br\s*\/?>|&nbsp;|\u00a0)\s*)*<\/p>/gi,
      ""
    )
    .trim()

const extractS3BucketAndKeyFromUrl = (imageUrl) => {
  if (!imageUrl) return null
  const trimmedUrl = imageUrl.trim()
  if (!trimmedUrl) return null

  try {
    const urlObj = new URL(trimmedUrl)
    const bucketHostPattern =
      /^(.+?)\.s3(?:[.-](?:dualstack\.)?[a-z0-9-]+)?\.amazonaws\.com$/i
    const pathStylePattern =
      /^s3(?:[.-](?:dualstack\.)?[a-z0-9-]+)?\.amazonaws\.com$/i

    const pathname = urlObj.pathname.replace(/^\/+/, "")
    if (!pathname) return null

    const virtualMatch = urlObj.hostname.match(bucketHostPattern)
    if (virtualMatch) {
      return { bucket: virtualMatch[1], key: pathname }
    }

    if (urlObj.hostname.match(pathStylePattern)) {
      const segments = pathname.split("/").filter(Boolean)
      if (segments.length < 2) return null
      const [bucket, ...keyParts] = segments
      return {
        bucket,
        key: keyParts.join("/"),
      }
    }

    return null
  } catch (error) {
    const trimmed = trimmedUrl.replace(/^\/+/, "")
    if (!trimmed || trimmed.includes(" ")) return null
    if (!trimmed.includes("/")) return null
    if (!DEFAULT_UPLOAD_BUCKET) return null
    return {
      bucket: DEFAULT_UPLOAD_BUCKET,
      key: trimmed,
    }
  }
}

const EventDialog = ({ open, event, onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false)
  const [selectedImageMedia, setSelectedImageMedia] = useState(null) // Store full media object with urlDto
  const [previewUrl, setPreviewUrl] = useState(null)
  const [tierPrograms, setTierPrograms] = useState([])
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [categoriesError, setCategoriesError] = useState(null)
  const [tierProgramsLoading, setTierProgramsLoading] = useState(true)
  const [tierProgramsError, setTierProgramsError] = useState(null)
  const [zoomCreating, setZoomCreating] = useState(false)
  const [audienceEstimate, setAudienceEstimate] = useState(null)
  const [audienceEstimateLoading, setAudienceEstimateLoading] = useState(false)
  const [audienceEstimateError, setAudienceEstimateError] = useState(null)
  const [newMemberEstimate, setNewMemberEstimate] = useState(null)
  const [newMemberEstimateLoading, setNewMemberEstimateLoading] = useState(false)
  const [newMemberEstimateError, setNewMemberEstimateError] = useState(null)
  const { enqueueSnackbar } = useSnackbar()
  const isMountedRef = useRef(true)
  
  const [openMediaManager, mediaManager] = MediaManager()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    unregister,
    clearErrors,
    formState: { errors },
  } = useForm()

  const watchedImageUrl = watch("imageUrl")
  const watchedTierProgramId = watch("tierProgramId")
  const watchedProductProgramId = watch("productProgramId")
  const watchedAudienceEmailsEnabled = watch("audienceEmailsEnabled")
  const watchedNewMemberEmailsEnabled = watch("newMemberEmailsEnabled")
  const watchedPlatform = watch("platform")
  const watchedTitle = watch("title")
  const watchedStartDate = watch("startDate")
  const watchedEndDate = watch("endDate")
  const watchedTimezone = watch("timezone")
  const watchedShortDescription = watch("shortDescription")
  const watchedDescription = watch("description")
  const selectedEventTier = useMemo(
    () =>
      tierPrograms.find(
        (tier) => Number(tier.id) === Number(watchedTierProgramId)
      ),
    [tierPrograms, watchedTierProgramId]
  )
  const selectedEventTierType = String(
    selectedEventTier?.type || ""
  ).toLowerCase()
  const selectedEventProduct = useMemo(
    () =>
      products.find(
        (product) =>
          Number(product?.programId) === Number(watchedProductProgramId)
      ),
    [products, watchedProductProgramId]
  )
  const newMemberAudienceDescription =
    selectedEventTierType === "product"
      ? watchedProductProgramId
        ? `Invites members during their first 21 days of access to ${
            selectedEventProduct?.name || `product ${watchedProductProgramId}`
          }. Other products and membership tiers are excluded.`
        : "Select a product to define the new-member audience."
      : selectedEventTierType === "platinum"
        ? "Invites active paid Platinum members during their first 21 days of qualifying access."
        : selectedEventTierType === "gold"
          ? "Invites active paid Gold and Platinum members during their first 21 days of qualifying access."
          : "Invites new paid members who meet this event's selected tier requirement during their first 21 days of qualifying access."

  const applyFreeTierDefault = () => {
    try {
      const freeTier = Array.isArray(tierPrograms)
        ? tierPrograms.find((t) => String(t?.type || "").toLowerCase() === "free")
        : null
      if (freeTier?.id) {
        setValue("tierProgramId", freeTier.id, { shouldDirty: true, shouldValidate: true })
        setValue("productProgramId", null, { shouldDirty: true, shouldValidate: true })
      }
    } catch {
      // Non-fatal
    }
  }

  const onFormInvalid = (formErrors) => {
    const firstError = Object.values(formErrors)[0]
    const message =
      firstError?.message || "Please fix the highlighted fields before submitting"
    enqueueSnackbar(message, { variant: "error" })
  }

  const formatAudienceEstimate = () => {
    if (!audienceEstimate) return ""
    const total = Number(audienceEstimate.total || 0).toLocaleString()
    if (audienceEstimate.gateType === "product") {
      const product = products.find(
        (item) =>
          Number(item?.programId) ===
          Number(audienceEstimate.productProgramId)
      )
      const productName =
        product?.name || product?.title || "product-gated"
      return `${total} ${productName} user${Number(audienceEstimate.total) === 1 ? "" : "s"} will receive the series.`
    }
    const breakdown = (audienceEstimate.breakdown || [])
      .filter((item) => Number(item.count) > 0)
      .map(
        (item) =>
          `${Number(item.count).toLocaleString()} ${item.tier}`
      )
      .join(", ")
    return `${total} eligible non-registrant${Number(audienceEstimate.total) === 1 ? "" : "s"} will receive the series${breakdown ? `: ${breakdown}` : ""}.`
  }

  const formatNewMemberEstimate = () => {
    if (!newMemberEstimate) return ""
    const total = Number(newMemberEstimate.total || 0).toLocaleString()
    if (newMemberEstimate.gateType === "product") {
      const productName =
        selectedEventProduct?.name ||
        `product ${newMemberEstimate.productProgramId || watchedProductProgramId}`
      return `${total} new ${productName} member${Number(newMemberEstimate.total) === 1 ? "" : "s"} will receive the series.`
    }
    const breakdown = (newMemberEstimate.breakdown || [])
      .filter((item) => Number(item.count) > 0)
      .map(
        (item) =>
          `${Number(item.count).toLocaleString()} ${item.tier}`
      )
      .join(", ")
    return `${total} new paid member${Number(newMemberEstimate.total) === 1 ? "" : "s"} will receive the series${breakdown ? `: ${breakdown}` : ""}.`
  }

  useEffect(() => {
    const selectedTier = tierPrograms.find(
      (t) => Number(t.id) === Number(watchedTierProgramId)
    )
    const isProductTier =
      selectedTier && String(selectedTier.type).toLowerCase() === "product"
    if (!isProductTier) {
      unregister("productProgramId")
      clearErrors("productProgramId")
    }
  }, [watchedTierProgramId, tierPrograms, unregister, clearErrors])

  useEffect(() => {
    if (!open || !event || tierProgramsLoading || tierProgramsError) return
    if (!watchedTierProgramId) {
      applyFreeTierDefault()
    }
  }, [open, event, tierPrograms, tierProgramsLoading, tierProgramsError, watchedTierProgramId])

  useEffect(() => {
    if (!open || !watchedAudienceEmailsEnabled || !watchedTierProgramId) {
      setAudienceEstimate(null)
      setAudienceEstimateError(null)
      setAudienceEstimateLoading(false)
      return
    }

    const selectedTier = tierPrograms.find(
      (tier) => Number(tier.id) === Number(watchedTierProgramId)
    )
    const isProductTier =
      selectedTier && String(selectedTier.type).toLowerCase() === "product"
    if (isProductTier && !watchedProductProgramId) {
      setAudienceEstimate(null)
      setAudienceEstimateError(null)
      return
    }

    let canceled = false
    const timer = setTimeout(async () => {
      setAudienceEstimateLoading(true)
      setAudienceEstimateError(null)
      try {
        const response = await getAudienceEmailEstimate({
          tierProgramId: Number(watchedTierProgramId),
          productProgramId: isProductTier
            ? Number(watchedProductProgramId)
            : undefined,
          eventId: event?.id,
        })
        if (canceled) return
        const estimate = response?.data ?? response
        setAudienceEstimate(estimate)
      } catch (error) {
        if (canceled) return
        setAudienceEstimate(null)
        setAudienceEstimateError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to estimate recipients"
        )
      } finally {
        if (!canceled) setAudienceEstimateLoading(false)
      }
    }, 350)

    return () => {
      canceled = true
      clearTimeout(timer)
    }
  }, [
    open,
    event?.id,
    watchedAudienceEmailsEnabled,
    watchedTierProgramId,
    watchedProductProgramId,
    tierPrograms,
  ])

  useEffect(() => {
    if (!open || !watchedNewMemberEmailsEnabled || !watchedTierProgramId) {
      setNewMemberEstimate(null)
      setNewMemberEstimateError(null)
      setNewMemberEstimateLoading(false)
      return
    }

    const isProductTier = selectedEventTierType === "product"
    if (isProductTier && !watchedProductProgramId) {
      setNewMemberEstimate(null)
      setNewMemberEstimateError(null)
      setNewMemberEstimateLoading(false)
      return
    }

    let canceled = false
    const timer = setTimeout(async () => {
      setNewMemberEstimateLoading(true)
      setNewMemberEstimateError(null)
      try {
        const response = await getAudienceEmailEstimate({
          tierProgramId: Number(watchedTierProgramId),
          productProgramId: isProductTier
            ? Number(watchedProductProgramId)
            : undefined,
          eventId: event?.id,
          seriesType: "new_member",
        })
        if (canceled) return
        setNewMemberEstimate(response?.data ?? response)
      } catch (error) {
        if (canceled) return
        setNewMemberEstimate(null)
        setNewMemberEstimateError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to estimate new-member recipients"
        )
      } finally {
        if (!canceled) setNewMemberEstimateLoading(false)
      }
    }, 350)

    return () => {
      canceled = true
      clearTimeout(timer)
    }
  }, [
    open,
    event?.id,
    watchedNewMemberEmailsEnabled,
    watchedTierProgramId,
    watchedProductProgramId,
    selectedEventTierType,
  ])

  useEffect(() => {
    if (!watchedImageUrl) {
      setPreviewUrl(null)
      return
    }

    if (
      selectedImageMedia?.urlDto?.originalUrl ||
      selectedImageMedia?.urlDto?.imgUrl
    ) {
      setPreviewUrl(null)
      return
    }

    const parsed = extractS3BucketAndKeyFromUrl(watchedImageUrl)
    if (!parsed) {
      setPreviewUrl(watchedImageUrl)
      return
    }

    let canceled = false

    httpPost("admin/systemSetting/getSignedUrl", {
      bucket: parsed.bucket,
      key: parsed.key,
    })
      .then((response) => {
        if (!isMountedRef.current || canceled) return
        const signedUrl = response?.data?.url || response?.url
        setPreviewUrl(signedUrl || watchedImageUrl)
      })
      .catch(() => {
        if (!isMountedRef.current || canceled) return
        setPreviewUrl(watchedImageUrl)
      })

    return () => {
      canceled = true
    }
  }, [
    watchedImageUrl,
    selectedImageMedia?.urlDto?.originalUrl,
    selectedImageMedia?.urlDto?.imgUrl,
  ])

  const defaultValues = useMemo(() => ({
    title: "",
    description: "",
    shortDescription: "",
    imageUrl: "",
    startDate: "",
    endDate: "",
    registrationStartDate: "",
    registrationEndDate: "",
    maxParticipants: "",
    tierProgramId: null,
    productProgramId: null,
    categoryId: null,
    eventType: "workshop",
    platform: "zoom",
    zoomJoinLink: "",
    isFeatured: false,
    isActive: true,
    sortOrder: 1,
    autoRegisterExternal: false,
    audienceEmailsEnabled: false,
    newMemberEmailsEnabled: false,
    tags: [],
    location: "",
    timezone: "America/Los_Angeles",
    externalRegistrationData: {},
    status: 0,
    // AEvent IDs and Internal Event UIDs
    hubspotEventRegistrantId: "",
    hubspotEventProductId: "",
    hubspotEventId: "",
    aeventCalendarId: "",
    aeventWtlId: "",
  }), [])

  // Load tier programs on component mount
  useEffect(() => {
    const loadTierPrograms = async () => {
      try {
        if (isMountedRef.current) {
          setTierProgramsLoading(true)
          setTierProgramsError(null)
        }
        const response = await getTierPrograms()

        if (!isMountedRef.current) return

        // Extract the data array from the response
        const programs = response.data || response

        // Ensure programs is an array
        if (Array.isArray(programs)) {
          setTierPrograms(programs)
        } else {
          throw new Error("Tier programs API returned non-array data")
        }

        // Load products (product gates) for "Product (select below)" tier
        try {
          const prod = await getProducts()
          if (isMountedRef.current) {
            setProducts(Array.isArray(prod) ? prod : [])
          }
        } catch {
          if (isMountedRef.current) setProducts([])
        }
      } catch (error) {
        if (!isMountedRef.current) return
        console.error("Failed to load tier programs:", error)
        setTierProgramsError(error.message)
        enqueueSnackbar("Failed to load tier programs", { variant: "error" })
      } finally {
        if (isMountedRef.current) {
          setTierProgramsLoading(false)
        }
      }
    }
    
    loadTierPrograms()
  }, [enqueueSnackbar])

  // Load categories for optional event categorization (Foundation/Business/Health/etc.)
  useEffect(() => {
    const loadCategories = async () => {
      if (!open) return
      try {
        if (isMountedRef.current) {
          setCategoriesLoading(true)
          setCategoriesError(null)
        }
        const resp = await getFullCategories()
        if (!isMountedRef.current) return
        const arr = (resp && (resp.data ?? resp)) || []
        if (Array.isArray(arr)) {
          setCategories(arr)
        } else {
          setCategories([])
        }
      } catch (e) {
        if (!isMountedRef.current) return
        setCategoriesError(e?.message || "Failed to load categories")
        setCategories([])
      } finally {
        if (isMountedRef.current) setCategoriesLoading(false)
      }
    }
    loadCategories()
  }, [open])

  useEffect(() => {
    isMountedRef.current = true
    
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (open) {
      if (event) {
        // Edit mode - load event data
        if (isMountedRef.current) {
          setLoading(true)
        }
        getEventById(event.id)
          .then((response) => {
            if (!isMountedRef.current) return
            
            verifyResponse(response.data)
            const eventData = response.data.data

            const tierProgramId = eventData.tierProgramId ?? (eventData.tierProgram ? eventData.tierProgram.id : null)
            const productProgramId = eventData.productProgramId ?? null
            
            // Get the event timezone for date conversion
            const eventTimezone = normalizeTimezone(eventData.timezone)
            
            reset({
              ...defaultValues,
              ...eventData,
              tierProgramId: tierProgramId,
              productProgramId: productProgramId,
              timezone: eventTimezone,
              // Convert UTC dates from backend to event timezone for datetime-local input display
              startDate: convertUTCToTimezoneForInput(eventData.startDate, eventTimezone),
              endDate: convertUTCToTimezoneForInput(eventData.endDate, eventTimezone),
              registrationStartDate: convertUTCToTimezoneForInput(eventData.registrationStartDate, eventTimezone),
              registrationEndDate: convertUTCToTimezoneForInput(eventData.registrationEndDate, eventTimezone),
              isFeatured: Boolean(eventData.isFeatured),
              isActive: Boolean(eventData.isActive),
              autoRegisterExternal: Boolean(eventData.autoRegisterExternal),
              audienceEmailsEnabled: Boolean(eventData.audienceEmailsEnabled),
              newMemberEmailsEnabled: Boolean(eventData.newMemberEmailsEnabled),
              tags: eventData.tags || [],
              // AEvent IDs and Internal Event UIDs
              hubspotEventRegistrantId: eventData.hubspotEventRegistrantId || "",
              hubspotEventProductId: eventData.hubspotEventProductId || "",
              hubspotEventId: eventData.hubspotEventId || "",
              aeventCalendarId: eventData.aeventCalendarId || "",
              aeventWtlId: eventData.aeventWtlId || "",
            })

            if (!tierProgramId) {
              applyFreeTierDefault()
            }

            // If there's an imageUrl, fetch the resource details to get variant info
            if (eventData.imageUrl) {
              try {
                const urlObj = new URL(eventData.imageUrl)
                const pathname = urlObj.pathname.startsWith('/')
                  ? urlObj.pathname.slice(1)
                  : urlObj.pathname

                if (!pathname || !pathname.startsWith('image/')) {
                  setSelectedImageMedia(null)
                } else {
                  const baseAwsKey = pathname

                  searchResource({
                    awsKey: baseAwsKey,
                    type: 1,
                    isArchive: 0,
                    minFileSize: 0,
                    maxFileSize: 2147483647,
                    sortBy: 'createdAt',
                    sortDir: 'DESC',
                  })
                    .then((searchResponse) => {
                      if (!isMountedRef.current) return

                      // searchResource returns response.data which is an array from adminQueryResource
                      // But verifyResponse might wrap it, so handle both cases
                      const resourceList = Array.isArray(searchResponse) 
                        ? searchResponse 
                        : (Array.isArray(searchResponse?.data) 
                            ? searchResponse.data 
                            : (searchResponse?.list || []))
                      
                      const matchingResource = resourceList.find(
                        (resource) => resource?.awsKey === baseAwsKey
                      )

                      if (matchingResource) {
                        setSelectedImageMedia(matchingResource)
                      } else {
                        // Fallback: Construct urlDto from imageUrl if resource doesn't exist
                        // This happens when the image was uploaded but resource record wasn't created yet
                        // or when the imageUrl points to a variant URL
                        setSelectedImageMedia(null)
                      }
                    })
                    .catch((searchError) => {
                      if (!isMountedRef.current) return
                      console.error("Error searching for resource:", searchError)
                      // Don't show error to user, just log it
                      setSelectedImageMedia(null)
                    })
                }
              } catch (urlError) {
                console.error("Failed to parse image URL:", urlError)
                setSelectedImageMedia(null)
              }
            } else {
              setSelectedImageMedia(null)
            }
          })
          .catch((error) => {
            if (!isMountedRef.current) return
            console.error("Error loading event:", error)
            enqueueSnackbar("Failed to load event details", { variant: "error" })
          })
          .finally(() => {
            if (isMountedRef.current) {
              setLoading(false)
            }
          })
      } else {
        // Create mode - reset to defaults
        reset(defaultValues)
        setSelectedImageMedia(null) // Clear selected media
        applyFreeTierDefault()
      }
    }
  }, [open, event, reset, enqueueSnackbar, defaultValues, tierPrograms, setValue])

  // Helper function to convert datetime from event timezone to UTC
  const convertToUTC = (dateTimeString, eventTimezone) => {
    if (!dateTimeString) return dateTimeString
    if (!eventTimezone || eventTimezone === "UTC") {
      // datetime-local values have no timezone; treat as UTC (not browser-local)
      const raw = String(dateTimeString).trim()
      const utcIso = raw.includes('Z') ? raw : `${raw}:00Z`
      return new Date(utcIso).toISOString()
    }
    
    // Parse the datetime-local value (format: "2024-01-15T10:00")
    const [datePart, timePart] = dateTimeString.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    const [hours, minutes] = (timePart || '00:00').split(':').map(Number)
    
    // Create moment in event timezone, then convert to UTC
    const momentInEventTz = moment.tz(
      {
        year,
        month: month - 1, // moment months are 0-indexed
        day,
        hour: hours,
        minute: minutes
      },
      eventTimezone
    )
    
    return momentInEventTz.utc().toISOString()
  }

  const handleFormSubmit = async (data) => {
    setLoading(true)
    try {
      // Check if tier programs are loaded
      if (tierProgramsError) {
        throw new Error("Cannot submit form: Tier programs failed to load")
      }

      const tierProgramId = data.tierProgramId ? Number(data.tierProgramId) : null
      if (!tierProgramId) {
        throw new Error("Tier Program is required")
      }
      const selectedTier = tierPrograms.find((t) => Number(t.id) === Number(tierProgramId))
      const isProductTier = selectedTier && String(selectedTier.type).toLowerCase() === 'product'
      const productProgramId = data.productProgramId ? Number(data.productProgramId) : null
      if (isProductTier && !productProgramId) {
        throw new Error("Product is required when Tier Program is set to Product")
      }

      const categoryId = data.categoryId ? Number(data.categoryId) : null
      
      // Convert dates from event timezone to UTC
      const selectedTimezone = data.timezone || "UTC"
      const startDateUTC = convertToUTC(data.startDate, selectedTimezone)
      const endDateUTC = convertToUTC(data.endDate, selectedTimezone)
      const registrationStartDateUTC = data.registrationStartDate
        ? convertToUTC(data.registrationStartDate, selectedTimezone)
        : null
      const registrationEndDateUTC = data.registrationEndDate
        ? convertToUTC(data.registrationEndDate, selectedTimezone)
        : null

      const eventData = {
        ...data,
        description: removeEmptyRichTextParagraphs(data.description),
        startDate: startDateUTC,
        endDate: endDateUTC,
        registrationStartDate: registrationStartDateUTC,
        registrationEndDate: registrationEndDateUTC,
        isFeatured: data.isFeatured ? 1 : 0,
        isActive: data.isActive ? 1 : 0,
        autoRegisterExternal: data.autoRegisterExternal ? 1 : 0,
        audienceEmailsEnabled: data.audienceEmailsEnabled ? 1 : 0,
        newMemberEmailsEnabled: data.newMemberEmailsEnabled ? 1 : 0,
        maxParticipants: data.maxParticipants ? parseInt(data.maxParticipants) : null,
        sortOrder: parseInt(data.sortOrder) || 1,
        externalRegistrationData: data.externalRegistrationData || {},
        tierProgramId: tierProgramId,
        productProgramId: isProductTier ? productProgramId : null,
        categoryId: categoryId,
        // AEvent IDs and Internal Event UIDs
        hubspotEventRegistrantId: data.hubspotEventRegistrantId || "",
        hubspotEventProductId: data.hubspotEventProductId || "",
        hubspotEventId: data.hubspotEventId || "",
        aeventCalendarId: data.aeventCalendarId || "",
        aeventWtlId: data.aeventWtlId || "",
      }
      
      let response
      if (event) {
        response = await updateEvent(event.id, eventData)
      } else {
        response = await createEvent(eventData)
      }

      verifyResponse(response.data)

      // HubSpot: track only on CREATE (not update)
      if (!event) {
        try {
          const created = response?.data?.data ?? response?.data ?? {}
          const createdId = created?.id ?? created?.eventId ?? null

          const accessTierLabel = (() => {
            const selectedTierType = selectedTier?.type ? String(selectedTier.type) : ""
            const isProduct = selectedTierType.trim().toLowerCase() === "product"
            if (isProduct) {
              const prod = Array.isArray(products)
                ? products.find((p) => Number(p?.programId) === Number(productProgramId))
                : null
              const prodLabel = prod?.name ? String(prod.name) : (productProgramId ? String(productProgramId) : "")
              return prodLabel ? `Product:${prodLabel}` : "Product"
            }
            return selectedTierType || (tierProgramId ? String(tierProgramId) : null)
          })()

          trackAdminEventCreated({
            event_title: eventData?.title || null,
            event_description: eventData?.description || null,
            event_url: eventData?.zoomJoinLink || eventData?.meetingUrl || null,
            platform_event_url: buildPlatformEventUrl(createdId),
            event_access_tier: accessTierLabel || null,
            event_image_url: eventData?.imageUrl || null,
            event_short_description: eventData?.shortDescription || null,
          })
        } catch (e) {
          // Non-fatal: do not block CMS UX if tracking fails
        }
      }

      enqueueSnackbar(
        event ? "Event updated successfully" : "Event created successfully",
        { variant: "success" }
      )
      onSubmit()
    } catch (error) {
      console.error("Error submitting event:", error)
      
      // Extract error message from response
      let errorMessage = event ? "Failed to update event" : "Failed to create event"
      if (error.response?.data?.message) {
        if (Array.isArray(error.response.data.message)) {
          errorMessage = error.response.data.message.join(", ")
        } else {
          errorMessage = error.response.data.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      enqueueSnackbar(errorMessage, { variant: "error" })
    }
    setLoading(false)
  }

  const getMediaUrl = (media) => {
    if (!media) return ""
    return (
      media.url ||
      media.urlDto?.imgUrl ||
      media.urlDto?.originalUrl ||
      ""
    )
  }

  const handleMediaSelectForField = (fieldName, media) => {
    // Use the provided fieldName (avoids stale closure over mediaField state)
    if (!media) return
    
    const selectedMedia = Array.isArray(media) && media.length > 0 ? media[0] : media
    const url = getMediaUrl(selectedMedia)

    setValue(fieldName, url, { shouldDirty: true, shouldValidate: true })
    
    // Store the full media object for preview with variants
    if (fieldName === "imageUrl") {
      setSelectedImageMedia(selectedMedia)
    }
  }

  const openMediaManagerForField = (fieldName) => {
    openMediaManager((media) => handleMediaSelectForField(fieldName, media), "image")
  }

  const handleCreateZoomMeeting = async () => {
    if (!watchedTitle?.trim()) {
      enqueueSnackbar("Event title is required before creating a Zoom meeting", { variant: "warning" })
      return
    }
    if (!watchedStartDate) {
      enqueueSnackbar("Start date is required before creating a Zoom meeting", { variant: "warning" })
      return
    }

    setZoomCreating(true)
    try {
      const response = await createZoomMeeting({
        title: watchedTitle.trim(),
        startDate: watchedStartDate,
        endDate: watchedEndDate || undefined,
        timezone: watchedTimezone || "America/Los_Angeles",
        shortDescription: watchedShortDescription || "",
        description: watchedDescription || "",
      })
      const body = response?.data
      verifyResponse(body)

      if (body?.rtnCode && body.rtnCode !== 200) {
        throw new Error(body.message || `Request failed (code ${body.rtnCode})`)
      }

      const payload = body?.data ?? {}
      const joinLink = payload.zoomJoinLink || payload.joinUrl
      if (!joinLink) {
        throw new Error("Zoom meeting was created but no join link was returned")
      }

      setValue("zoomJoinLink", joinLink, { shouldDirty: true, shouldValidate: true })
      setValue("platform", "zoom", { shouldDirty: true, shouldValidate: true })
      if (payload.externalRegistrationData) {
        setValue("externalRegistrationData", payload.externalRegistrationData, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }

      enqueueSnackbar("Zoom meeting created — join link added to the form", { variant: "success" })
    } catch (error) {
      console.error("Error creating Zoom meeting:", error)
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create Zoom meeting"
      enqueueSnackbar(
        Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage,
        { variant: "error" }
      )
    } finally {
      setZoomCreating(false)
    }
  }

  return (
    <>
      <StyledEventDialog>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>
            <div className="dialog-title-group">
              <h2 className="dialog-title">
                {event ? "Edit Event" : "Create New Event"}
              </h2>
              <p className="dialog-subtitle">
                Configure schedule, access controls, and metadata for this event.
              </p>
            </div>
          </DialogTitle>
          <form onSubmit={handleSubmit(handleFormSubmit, onFormInvalid)}>
            <DialogContent>
              <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography className="dialog-section-title">
                  Basic Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: "Title is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Event Title"
                      fullWidth
                      error={!!errors.title}
                      helperText={errors.title?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="eventType"
                  control={control}
                  rules={{ required: "Event type is required" }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.eventType}>
                      <InputLabel>Event Type</InputLabel>
                      <Select {...field} label="Event Type">
                        {EVENT_TYPE_LIST.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="shortDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Short Description"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      label="Full Description"
                      placeholder="Enter detailed description with formatting options..."
                      toolbarConfig="description"
                      minHeight="150px"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="imageUrl"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <TextField
                        {...field}
                        label="Event Image URL"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <Button
                              size="small"
                              onClick={() => openMediaManagerForField("imageUrl")}
                            >
                              Browse
                            </Button>
                          ),
                        }}
                      />
                      {(watchedImageUrl || selectedImageMedia?.urlDto?.originalUrl) && (
                        <div className="image-preview-panel">
                          <p className="image-preview-label">Image Preview</p>
                          <div className="image-preview-wrap">
                            <img
                              src={
                                selectedImageMedia?.urlDto?.originalUrl ||
                                selectedImageMedia?.urlDto?.imgUrl ||
                                previewUrl ||
                                watchedImageUrl
                              }
                              alt="Event Preview"
                              className="image-preview"
                              onError={(e) => {
                                e.target.style.border = "2px solid #f44336"
                                e.target.alt = "Unable to load preview"
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Location"
                      fullWidth
                      placeholder="Virtual, Physical location, etc."
                    />
                  )}
                />
              </Grid>

              {/* Date and Time */}
              <Grid item xs={12}>
                <Typography className="dialog-section-title">
                  Date and Time
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="timezone"
                  control={control}
                  rules={{ required: "Timezone is required" }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.timezone}>
                      <InputLabel>Event Timezone</InputLabel>
                      <Select {...field} label="Event Timezone">
                        {TIMEZONE_LIST.map((tz) => (
                          <MenuItem key={tz.value} value={tz.value}>
                            {tz.text}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Select the timezone for this event. Times below will be entered in this timezone.
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                {/* Empty grid for spacing */}
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="startDate"
                  control={control}
                  rules={{ required: "Start date is required" }}
                  render={({ field }) => {
                    const selectedTimezone = watch("timezone") || "UTC"
                    const timezoneLabel = TIMEZONE_LIST.find(t => t.value === selectedTimezone)?.text || selectedTimezone
                    return (
                      <TextField
                        {...field}
                        label="Start Date & Time"
                        type="datetime-local"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.startDate}
                        helperText={errors.startDate?.message || `Enter time in ${timezoneLabel}`}
                      />
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="endDate"
                  control={control}
                  rules={{ required: "End date is required" }}
                  render={({ field }) => {
                    const selectedTimezone = watch("timezone") || "UTC"
                    const timezoneLabel = TIMEZONE_LIST.find(t => t.value === selectedTimezone)?.text || selectedTimezone
                    return (
                      <TextField
                        {...field}
                        label="End Date & Time"
                        type="datetime-local"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.endDate}
                        helperText={errors.endDate?.message || `Enter time in ${timezoneLabel}`}
                      />
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="registrationStartDate"
                  control={control}
                  render={({ field }) => {
                    const selectedTimezone = watch("timezone") || "UTC"
                    const timezoneLabel = TIMEZONE_LIST.find(t => t.value === selectedTimezone)?.text || selectedTimezone
                    return (
                      <TextField
                        {...field}
                        label="Registration Start Date"
                        type="datetime-local"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText={`Enter time in ${timezoneLabel}`}
                      />
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="registrationEndDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Registration End Date"
                      type="datetime-local"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!categoriesError}>
                      <InputLabel>Category (optional)</InputLabel>
                      <Select
                        {...field}
                        label="Category (optional)"
                        disabled={categoriesLoading}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const v = e.target.value
                          field.onChange(v === "" ? null : Number(v))
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        {Array.isArray(categories) && categories.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.fullName || c.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Link this event to an existing platform category (e.g., Foundation, Business). Leave blank to hide Category on the frontend.
                      </FormHelperText>
                      {categoriesError ? (
                        <FormHelperText error>{categoriesError}</FormHelperText>
                      ) : null}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Access and Settings */}
              <Grid item xs={12}>
                <Typography className="dialog-section-title">
                  Access and Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="tierProgramId"
                  control={control}
                  rules={{ required: "Tier Program is required" }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.tierProgramId || !!tierProgramsError}>
                      <InputLabel>Tier Program</InputLabel>
                      <Select
                        {...field}
                        label="Tier Program"
                        disabled={tierProgramsLoading || !!tierProgramsError}
                        onChange={(e) => {
                          field.onChange(e)
                          const nextId = Number(e.target.value)
                          const selectedTier = tierPrograms.find((t) => Number(t.id) === nextId)
                          if (!selectedTier || String(selectedTier.type).toLowerCase() !== "product") {
                            setValue("productProgramId", null, { shouldDirty: true, shouldValidate: true })
                            unregister("productProgramId")
                            clearErrors("productProgramId")
                          }
                        }}
                      >
                        {tierProgramsLoading ? (
                          <MenuItem disabled>Loading tiers...</MenuItem>
                        ) : tierProgramsError ? (
                          <MenuItem disabled>Failed to load tiers</MenuItem>
                        ) : (
                          [...tierPrograms]
                            .sort((a, b) => {
                              const order = { Free: 0, Silver: 1, Gold: 2, Platinum: 3, product: 4 }
                              const aIdx = order[a?.type] ?? 99
                              const bIdx = order[b?.type] ?? 99
                              return aIdx - bIdx
                            })
                            .map((tier) => (
                              <MenuItem key={tier.id} value={tier.id}>
                                {String(tier.type).toLowerCase() === 'product'
                                  ? 'Product (select below)'
                                  : tier.type}
                              </MenuItem>
                            ))
                        )}
                      </Select>
                      {tierProgramsError && (
                        <FormHelperText error>
                          {tierProgramsError}
                        </FormHelperText>
                      )}
                      {errors.tierProgramId?.message ? (
                        <FormHelperText error>
                          {errors.tierProgramId.message}
                        </FormHelperText>
                      ) : null}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                {(() => {
                  const selectedTier = tierPrograms.find((t) => Number(t.id) === Number(watch('tierProgramId')))
                  const isProductTier = selectedTier && String(selectedTier.type).toLowerCase() === 'product'
                  if (!isProductTier) return null
                  return (
                    <Controller
                      name="productProgramId"
                      control={control}
                      rules={{ required: "Product is required" }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.productProgramId}>
                          <InputLabel>Product</InputLabel>
                          <Select {...field} label="Product">
                            {products.map((p) => (
                              <MenuItem key={p.programId} value={p.programId}>
                                {p.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Select the product gate required to access this event.
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  )
                })()}
                <Controller
                  name="maxParticipants"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Max Participants"
                      type="number"
                      fullWidth
                      placeholder="Leave empty for unlimited"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="platform"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Platform</InputLabel>
                      <Select {...field} label="Platform">
                        {EVENT_PLATFORM_LIST.map((platform) => (
                          <MenuItem key={platform.value} value={platform.value}>
                            {platform.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="sortOrder"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Sort Order"
                      type="number"
                      fullWidth
                      defaultValue={1}
                    />
                  )}
                />
              </Grid>

              {/* AEvent IDs and Internal Event UIDs */}
              <Grid item xs={12}>
                <Typography className="dialog-section-title">
                Meeting Details
                </Typography>
              </Grid>

              {watchedPlatform === "zoom" && (
                <>
                  <Grid item xs={12}>
                    <Controller
                      name="zoomJoinLink"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Zoom Join Link"
                          fullWidth
                          placeholder="https://zoom.us/j/..."
                          helperText="Paste an existing Zoom URL or click Create Zoom Meeting below."
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={handleCreateZoomMeeting}
                        disabled={zoomCreating || loading}
                      >
                        {zoomCreating ? "Creating Zoom..." : "Create Zoom Meeting"}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Creates a scheduled Zoom meeting with NeuroGym default settings and co-hosts.
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6}>
                <Controller
                  name="hubspotEventRegistrantId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="HubSpot Event Registrant ID"
                      fullWidth
                      placeholder="e.g., event_business_success_rid"
                      helperText="Used for auto-registration in AEvent logic"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="hubspotEventProductId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="HubSpot Event Product ID"
                      fullWidth
                      placeholder="e.g., wtgb"
                      helperText="Product identifier for HubSpot"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="hubspotEventId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="HubSpot Event ID"
                      fullWidth
                      placeholder="e.g., 2025-09-09_HIRE-AFFORDABLE-DEV"
                      helperText="Unique event identifier for HubSpot"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="aeventCalendarId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="AEvent Calendar ID"
                      fullWidth
                      placeholder="e.g., WTGB-BS"
                      helperText="Calendar identifier for event association"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="aeventWtlId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="AEvent WTL ID"
                      fullWidth
                      placeholder="e.g., eIafWsAUWzJOUDl"
                      helperText="AEvent wait-to-live identifier"
                    />
                  )}
                />
              </Grid>

              {/* Tags */}
              <Grid item xs={12}>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <TagsInput
                      tags={field.value || []}
                      setTags={field.onChange}
                    />
                  )}
                />
              </Grid>

              {/* Switches */}
              <Grid item xs={12}>
                <Typography className="dialog-section-title">
                  Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="isFeatured"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Featured Event"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Active"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="autoRegisterExternal"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Auto Register External"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="audienceEmailsEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        }
                        label="Eligible Member Email Series"
                      />
                      <FormHelperText>
                        Enables the eligible non-registrant track shown below.
                      </FormHelperText>
                      <EventEmailCadence
                        audienceEnabled={Boolean(field.value)}
                        newMemberEnabled={Boolean(watchedNewMemberEmailsEnabled)}
                        compact
                        defaultExpanded={false}
                      />
                      {audienceEstimateLoading && (
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                          <CircularProgress size={16} />
                          <Typography variant="body2">
                            Estimating eligible recipients…
                          </Typography>
                        </Box>
                      )}
                      {!audienceEstimateLoading && audienceEstimateError && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          {audienceEstimateError}
                        </Alert>
                      )}
                      {!audienceEstimateLoading && audienceEstimate && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <strong>{formatAudienceEstimate()}</strong>
                          <br />
                          This is a live estimate. Access and registration changes
                          before each send can change the final count.
                        </Alert>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="newMemberEmailsEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        }
                        label="New Member Email Series Invite"
                      />
                      <FormHelperText>
                        {newMemberAudienceDescription}
                        {selectedEventTierType !== "product" &&
                          " Gold-to-Platinum upgrades do not restart the membership clock."}
                      </FormHelperText>
                      {newMemberEstimateLoading && (
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                          <CircularProgress size={16} />
                          <Typography variant="body2">
                            Estimating new-member recipients…
                          </Typography>
                        </Box>
                      )}
                      {!newMemberEstimateLoading && newMemberEstimateError && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          {newMemberEstimateError}
                        </Alert>
                      )}
                      {!newMemberEstimateLoading && newMemberEstimate && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <strong>{formatNewMemberEstimate()}</strong>
                          <br />
                          Registrants are excluded at every send. Members who
                          also qualify for the eligible audience series receive
                          only one email per window.
                        </Alert>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Status (only for edit mode) */}
              {event && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status">
                          {EVENT_STATUS_LIST.map((status) => (
                            <MenuItem key={status.value} value={status.value}>
                              {status.text}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                className="dialog-cancel-btn"
                onClick={onClose}
                variant="outlined"
                size="medium"
              >
                Cancel
              </Button>
              <Button
                className="dialog-submit-btn"
                type="submit"
                variant="contained"
                size="medium"
                disabled={loading}
              >
                {event ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </StyledEventDialog>

      {mediaManager}
      <LoadingScreen loading={open && (loading || tierProgramsLoading)} />
    </>
  )
}

EventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  event: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default EventDialog
